import styled from '@emotion/styled'
import { getFormValues, InjectedFormProps, reduxForm } from 'redux-form'
import { DatePickerField, InputField, UploadfileField, SelectField } from '../../components/common/fields'
import Card from '../../components/common/Card'
import Text from '../../components/common/Text'
import { mobile, mobileVerySmall } from '../../utils/responsive-helper'
import moment from 'moment'
import FooterContent from '../../components/FooterContent'
import Button from '../../components/common/Button'
import { EClaimFormParams, EClaimItem } from '../../services/e-claim/e-claim-types'
import receiptIcon from '../../assets/images/icons-slip-48-px.svg'
import { ComponentType, useMemo, useState } from 'react'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { EClaimInf, setClaimInfo } from '../../redux-store/e-claim-reducer'
import { useHistory } from 'react-router'
import * as paths from '../../constant/path'
import { InputLabel } from '../../components/common/Input'
import { FONT_BLACK, FONT_LIGHT_GRAY, INPUT_ERROR } from '../../constant/colors'
import { useGetUser, useGetUserCoin } from '../../services/user/user-query'
import { useCreateUserClaim, useUploadFile, useGetRelativeOptionScg } from '../../services/e-claim/e-claim-query'
import { Moment } from 'moment'
import Modal from '../../components/common/Modal'
import { useVisibility } from '../../utils/custom-hook'
import { numberWithCommas } from '../../utils/helper'
import { Divider } from 'antd'

const ClaimFormContainer = styled(Card)`
  margin-top: 32px;
  margin-bottom: 80px;
  padding: 32px;
  .layout-upload {
    ${mobile} {
      width: 100%;
    }
    ${mobileVerySmall} {
      width: 100%;
    }
  }
  ${mobile} {
    padding: 16px;
  }
  ${mobileVerySmall} {
    padding: 16px;
  }
`

const Spacer = styled.div`
  width: 20px;
  height: 20px;

  ${mobile} {
    display: none;
  }
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;

  ${mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 8px;

  ${mobile} {
    flex-direction: column;
  }
`

const CustomEditableFooter = styled(FooterContent)`
  height: 80;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 18px;
  padding-bottom: 18px;

  ${mobile} {
    justify-content: center;
  }
`

const CustomUnEditableFooter = styled(FooterContent)`
  height: 80;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 18px;
  padding-bottom: 18px;

  ${mobile} {
    flex-direction: column;
    justify-content: center;
  }
`

const SumPointContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;

  ${mobile} {
    margin-bottom: 20px;
    padding-left: 20px;
    padding-right: 20px;
  }
`

const SubmitButton = styled(Button)`
  width: 267px;
`
const TextModal = styled(Text)`
  text-align: center;
`

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 30%;
  /* border: 1px solid; */
  margin-bottom: 0;
  padding-bottom: 0;

  ${mobile} {
    width: 100%;
  }
`

export const ECLAIM_FORM_NAME = 'ECLAIM_FORM'
type EClaimParam = {
  eClaimItem?: EClaimItem
  editable?: boolean
  eClaimFormValue?: EClaimInf['claimInfo']
  isPreview?: boolean
  isHistory?: boolean
}
const required = (value: string) => (value ? undefined : 'error.required')
const requiredDate = (value: Moment) => {
  const x = moment(value).isValid() ? undefined : 'error.required'
  return x
}
const EClaimForm: ComponentType<
  EClaimParam & InjectedFormProps<EClaimFormParams, EClaimParam, string>
> = (props) => {
  const dispatch = useDispatch()
  const {
    handleSubmit,
    invalid,
    eClaimItem,
    editable = true,
    eClaimFormValue,
    isPreview = false,
    isHistory = false
  } = props

  const formValuesSelector = getFormValues(ECLAIM_FORM_NAME)
  const formValues = useSelector<any, EClaimFormParams>(
    (state) => formValuesSelector(state) as EClaimFormParams,
  )

  const [visible, modalAction] = useVisibility()
  const [errorMessage, setErrorMessage] = useState('')
  const { data: user } = useGetUser()
  const { firstName, lastName } = user || {}

  const fullname = `${firstName} ${lastName}`
  const history = useHistory()
  const { data: userCoin = 0 } = useGetUserCoin()
  const { mutate: createUserClaim, isLoading: isSubmitting } = useCreateUserClaim()
  const { mutateAsync: uploadFile, isLoading: isUploading } = useUploadFile()
  const { data: relativesOptions } = useGetRelativeOptionScg()

  const nameRelative = useCallback(
    (relativeTypeId) => {
      const data = relativesOptions?.map((relativesOption) => {
        const { value, label } = relativesOption
        if (relativeTypeId === value) {
          return label
        }
      })

      return data?.length === 0 ? '-' : (data?.[0] !== undefined ? data : '-')
    },

    [relativesOptions],
  )

  const amountFormated = useMemo(() => {
    return eClaimFormValue?.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }, [eClaimFormValue?.amount])

  const userRestCoinFormated = useMemo(() => {
    const restCoin = userCoin - (eClaimFormValue?.amount || 0)
    return restCoin.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }, [userCoin, eClaimFormValue])

  const convertBase64ToFile = useCallback(async (imageBase64: string) => {
    const res = await fetch(imageBase64)
    const blobFile = await res.blob()
    const file = new File([blobFile], `${Date.now()}.${blobFile.type.split('/')[1].split(';')[0]}`, { type: 'image/png' })
    return file
  }, [])

  const onSubmit = useCallback(
    (form) => {
      dispatch(
        setClaimInfo({
          name: form.name,
          paymentDate: moment(form.paymentDate),
          amount: form.amount,
          citizenIdImage: form.citizenIdImage,
          receiptImage: form.receiptImage,
          remark: form.remark,
          relativeTypeId: form.relativeTypeId,
        }),
      )
      if (editable === true) {
        history.push(paths.eClaimSummary({ routeParam: { eClaimId: eClaimItem?.claimId || 0 } }))
      }
    },
    [dispatch, eClaimItem?.claimId, editable, history],
  )

  const getImageUploadUrl = useCallback(async () => {
    const citizenImage = await convertBase64ToFile(eClaimFormValue?.citizenIdImage || '')
    const receiptImage = await convertBase64ToFile(eClaimFormValue?.receiptImage || '')
    const citizenImageURL = uploadFile(citizenImage)
    const receiptImageURL = uploadFile(receiptImage)
    return await Promise.all([citizenImageURL, receiptImageURL])
  }, [
    convertBase64ToFile,
    eClaimFormValue?.citizenIdImage,
    eClaimFormValue?.receiptImage,
    uploadFile,
  ])

  const onSubmitApplication = useCallback(async () => {
    //uploadfile
    try {
      const [citizenImageURL, receiptImageURL] = await getImageUploadUrl()

      createUserClaim(
        {
          claimId: eClaimItem?.claimId || 0,
          claimDate: eClaimFormValue?.paymentDate!,
          claimName: fullname,
          citizenFileUrl: citizenImageURL.link || '',
          receiptFile: eClaimFormValue?.receiptImage ? receiptImageURL.link : '',
          exchangeCoin: eClaimFormValue?.amount!,
          remark: eClaimFormValue?.remark || '',
          relativeTypeId: (eClaimFormValue?.relativeTypeId === null || eClaimFormValue?.relativeTypeId === '0') ? undefined : eClaimFormValue?.relativeTypeId,
        },
        {
          onSuccess: () => {
            history.push(
              paths.eClaimCompleted({
                routeParam: { eClaimId: eClaimItem?.claimId || 0 },
                queryParam: {
                  eClaimName: eClaimItem?.claimName || '',
                  eClaimPrice: eClaimFormValue?.amount || 0,
                },
              }),
            )
          },
          onError: (error) => {
            setErrorMessage(error.message)
            modalAction.show()
          },
        },
      )
    } catch (error) {
      console.log(error)
    }
  }, [
    createUserClaim,
    getImageUploadUrl,
    eClaimFormValue?.amount,
    eClaimFormValue?.paymentDate,
    eClaimFormValue?.receiptImage,
    eClaimFormValue?.remark,
    eClaimItem?.claimId,
    eClaimItem?.claimName,
    eClaimFormValue?.relativeTypeId,
    fullname,
    history,
    modalAction,
  ])

  const validateImage = useMemo(() => {
    const hasSomeImage = formValues?.citizenIdImage || formValues?.receiptImage
    return isPreview === false && !hasSomeImage ? [required] : undefined
  }, [formValues?.citizenIdImage, formValues?.receiptImage, isPreview])

  const validateClaimAmount = useCallback(
    (value) => {
      if (eClaimItem?.claimPrice && value > eClaimItem?.claimPrice) {
        return 'eClaim.claimPriceError'
      }
    },
    [eClaimItem?.claimPrice],
  )
  return (
    <>
      <Modal
        visible={visible}
        onCancel={() => {
          modalAction.hide()
        }}
        onCancelText="ตกลง"
      >
        <div style={{ margin: '30px' }}>
          <TextModal size={24} type="Bold">
            {errorMessage}
          </TextModal>
          <TextModal>กรุณาตรวจสอบอีกครั้ง</TextModal>
          <Divider />
        </div>
      </Modal>
      <ClaimFormContainer>
        <Text size={24} type="Bold">
          {editable ? 'กรอกรายละเอียดการเบิกสวัสดิการ' : 'ตรวจสอบรายละเอียด '}
        </Text>
        <InputContainer>
          {!editable && (
            <FlexCol>
              <InputLabel required={false} >ชื่อ - นามสกุล ผู้ขอเบิก</InputLabel>
              <Text type="Bold">{fullname}</Text>
            </FlexCol>
          )}
          <Spacer />
          <FlexCol>
            {editable && (
              <DatePickerField
                name="paymentDate"
                label="วันที่บนใบเสร็จ"
                validate={[requiredDate]}
                disabled={isHistory}
              />
            )}
            {!editable && <InputLabel required={false}>วันที่บนใบเสร็จ</InputLabel>}
            {!editable && (
              <Text type="Bold">{eClaimFormValue?.paymentDate.format('DD/MM/YYYY')}</Text>
            )}
          </FlexCol>
          <Spacer />
          <FlexCol>
            {editable && (
              <InputField
                name="amount"
                label="จำนวนที่ขอเบิก"
                placeholder="ใส่จำนวนคะแนน"
                validate={[required, validateClaimAmount]}
                inputType="number"
                disabled={isHistory}
              />
            )}
            {!editable && <InputLabel required={false}>จำนวนที่ขอเบิก</InputLabel>}
            {!editable && (
              <Text type="Bold">
                {eClaimFormValue?.amount ? numberWithCommas(Number(eClaimFormValue?.amount)) : 0}
              </Text>
            )}
          </FlexCol>

          <Spacer />

          <FlexCol>
            {(editable && !isHistory) && (
              <div>
                <InputLabel>ประเภทผู้เบิก </InputLabel>
                <SelectField
                  name="relativeTypeId"
                  placeholder="ประเภทผู้เบิก"
                  validate={[required]}
                  options={relativesOptions || []}
                // disabled={isHistory}
                />
              </div>
            )}

            {(!editable || isHistory) && <InputLabel>ประเภทผู้เบิก </InputLabel>}
            {(!editable || isHistory) && (
              <Text type="Bold">{nameRelative(eClaimFormValue?.relativeTypeId) || '-'}</Text>
            )}
          </FlexCol>

        </InputContainer>

        <InputContainer>
          <Spacer />

          <FlexCol>
            {editable && <InputField disabled={isHistory} name="remark" label="หมายเหตุ" placeholder="รายละเอียด" />}
            {!editable && <InputLabel required={false}>หมายเหตุ</InputLabel>}
            {!editable && (
              <Text type="Bold">{eClaimFormValue?.remark ? eClaimFormValue?.remark : '-'} </Text>
            )}
          </FlexCol>
        </InputContainer>

        <Text size={24} type="Bold">
          แนบเอกสาร
        </Text>
        <FileUploadContainer>
          {(eClaimFormValue?.citizenIdImage || isPreview === false) && (
            <UploadfileField
              name="citizenIdImage"
              icon={receiptIcon}
              title={
                '1. แนบเอกสารประกอบการเบิก เช่น ใบเสร็จใบรับรองแพทย์ หรือ เอกสารอื่นๆ ที่เกี่ยวข้อง (jpg, png ขนาดไม่เกิน 10 MB)'
              }
              desc={'(jpg, png ขนาดไม่เกิน 10 MB)'}
              value={eClaimFormValue?.citizenIdImage}
              isPreview={isPreview}
              validate={validateImage}
              maximumFileSize={10}
            />
          )}
          {(eClaimFormValue?.receiptImage || isPreview === false) && (
            <>
              <Spacer />
              <UploadfileField
                name="receiptImage"
                icon={receiptIcon}
                title={
                  '2. แนบเอกสารประกอบการเบิก เช่น ใบเสร็จใบรับรองแพทย์ หรือ เอกสารอื่นๆ ที่เกี่ยวข้อง (jpg, png ขนาดไม่เกิน 10 MB)'
                }
                desc={'(jpg, png ขนาดไม่เกิน 10 MB)'}
                value={eClaimFormValue?.receiptImage}
                isPreview={isPreview}
                maximumFileSize={10}
              />
            </>
          )}
        </FileUploadContainer>
      </ClaimFormContainer>
      {editable ? (
        <CustomEditableFooter>
          <SubmitButton onClick={handleSubmit(onSubmit)} disabled={invalid}>
            ถัดไป
          </SubmitButton>
        </CustomEditableFooter>
      ) : (
          <CustomUnEditableFooter>
            <SumPointContainer>
              <Text type="Bold">รวมใช้คะแนน</Text>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <Text type="Bold" color={INPUT_ERROR}>
                  -{amountFormated}
                </Text>
              &nbsp;
              <Text color={FONT_BLACK}>คะแนน</Text>&nbsp;
              <Text color={FONT_LIGHT_GRAY}>{`(คงเหลือ ${userRestCoinFormated} คะแนน)`}</Text>
              </div>
            </SumPointContainer>

            <SubmitButton onClick={onSubmitApplication} disabled={isSubmitting || isUploading || isHistory}>
              ยื่นคำขอ
          </SubmitButton>
          </CustomUnEditableFooter>
        )}
    </>
  )
}

export default reduxForm<EClaimFormParams, EClaimParam, string>({
  form: ECLAIM_FORM_NAME,
  initialValues: {
    // name: 'ประทุด จัดโอชา',
    // paymentDate: moment(),
  },
})(EClaimForm)
